export const PRESETS: { label: string; rule: string }[] = [
  { label: "Ежедневно", rule: "FREQ=DAILY" },
  { label: "Будни", rule: "FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR" },
  { label: "Еженедельно", rule: "FREQ=WEEKLY" },
  { label: "Ежемесячно", rule: "FREQ=MONTHLY" },
  { label: "Ежегодно", rule: "FREQ=YEARLY" },
];

export const WEEKDAYS = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"] as const;
export type Weekday = (typeof WEEKDAYS)[number];

export const WEEKDAY_LABELS: Record<Weekday, string> = {
  MO: "Пн", TU: "Вт", WE: "Ср", TH: "Чт", FR: "Пт", SA: "Сб", SU: "Вс",
};

export const ORDINAL_LABELS: { value: number; label: string }[] = [
  { value: 1, label: "первый" },
  { value: 2, label: "второй" },
  { value: 3, label: "третий" },
  { value: 4, label: "четвёртый" },
  { value: -1, label: "последний" },
];

export type Unit = "DAILY" | "WEEKLY" | "MONTHLY" | "YEARLY";
export type MonthlyMode = "day" | "weekday";

export interface RecurrenceParts {
  unit: Unit;
  interval: number;
  byday: string[];
  monthlyMode: MonthlyMode;
  monthDay: number;
  weekdayPos: number;
  weekdayName: string;
}

export function parseRule(rule: string | null): RecurrenceParts {
  const out: RecurrenceParts = {
    unit: "DAILY",
    interval: 1,
    byday: [],
    monthlyMode: "day",
    monthDay: 1,
    weekdayPos: 1,
    weekdayName: "MO",
  };
  if (!rule) return out;
  for (const pair of rule.split(";")) {
    const [k, v] = pair.split("=");
    if (k === "FREQ") out.unit = v as Unit;
    else if (k === "INTERVAL") out.interval = Math.max(1, parseInt(v, 10) || 1);
    else if (k === "BYMONTHDAY") {
      out.monthlyMode = "day";
      out.monthDay = Math.min(31, Math.max(1, parseInt(v, 10) || 1));
    } else if (k === "BYDAY") {
      const tokens = v.split(",");
      const ordinalMatch = tokens[0].match(/^(-?\d+)(MO|TU|WE|TH|FR|SA|SU)$/);
      if (tokens.length === 1 && ordinalMatch) {
        out.monthlyMode = "weekday";
        out.weekdayPos = parseInt(ordinalMatch[1], 10);
        out.weekdayName = ordinalMatch[2];
      } else {
        out.byday = tokens.filter((d) => (WEEKDAYS as readonly string[]).includes(d));
      }
    }
  }
  return out;
}

export function buildRule(parts: RecurrenceParts): string {
  const segs = [`FREQ=${parts.unit}`];
  if (parts.interval > 1) segs.push(`INTERVAL=${parts.interval}`);
  if (parts.unit === "WEEKLY" && parts.byday.length > 0) {
    segs.push(`BYDAY=${parts.byday.join(",")}`);
  }
  if (parts.unit === "MONTHLY") {
    if (parts.monthlyMode === "day") segs.push(`BYMONTHDAY=${parts.monthDay}`);
    else segs.push(`BYDAY=${parts.weekdayPos}${parts.weekdayName}`);
  }
  return segs.join(";");
}

export function isPresetRule(rule: string): boolean {
  return PRESETS.some((p) => p.rule === rule);
}

export function recurrenceShortLabel(rule: string | null): string | null {
  if (!rule) return null;
  const match = PRESETS.find((p) => p.rule === rule);
  return match ? match.label : "Настроить";
}
